import React, { useEffect, useState } from 'react';
import { FaGlasses } from 'react-icons/fa';
import CadenceTable from '../../components/common/CadenceTable';
import Resources from "../Projects/sections/Project/sections/ProjectBoxComponent/Resources";
import { getDashboardStatusColor, getDashboardStatusLabel, stageBgColorMap, stageTextColorMap } from '../../constants/integrationTypes';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import { FiAlertTriangle } from 'react-icons/fi';
import { Tooltip } from 'flowbite-react';
import { useSelector } from "react-redux";
import Progress from "../Projects/sections/Project/sections/Progress";
import Options from "../Projects/sections/Project/sections/Options";
import toast from "react-hot-toast";
import { useResourceInfo } from '../../hooks/useResourceInfo';
import TruncatedText from '../../components/common/TruncatedText';
import CustomTooltip from '../../components/common/CustomTooltip';
import { IntegrationStatusCell, IntegrationTypeCell, ProgressCell, StatusCell, TitleCell, ViewTitleSearchValue } from '../../shared/molecules/CadenceTable/CadenceTable';
import CadenceDataTable from '../../shared/molecules/CadenceDataTable/CadenceDataTable';
import WorkItemOptions from '../Projects/sections/Project/sections/WorkItemOptions';

function formatShortDate(dateStr: string) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  // Format as 'Mon DD' (e.g., Sept 18)
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric' });
}



const ListViewTable = ({ data, projectId, handleRowClick, handleProjectClick, onDelete, loading = false, openEditProjectModal }: any) => {
  const [sortedData, setSortedData] = useState<any[]>(data);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const {
    simulation
  } = useSelector((state: any) => state.timeline);

  const { getResourceName } = useResourceInfo();

  const hasIntegrationType = Array.isArray(data) && data.some((item) => item.integration_data);

  useEffect(() => {
    setSortedData(data);
  }, [data]);

  const handleCopyId = async (id: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      toast.success('Project ID copied to clipboard!');
      setTimeout(() => setCopiedId(null), 2000); // Clear after 2 seconds
    } catch (err) {
      console.error('Failed to copy ID:', err);
      toast.error('Failed to copy ID to clipboard');
    }
  };

  const tableHeadMap = [
    
    { key: 'status', label: 'Status' },
    { key: 'project_name', label: 'Name' },
    ...(hasIntegrationType ? [{ key: 'integration_type', label: 'Integration Type' }] : []),
    ...(hasIntegrationType ? [{ key: 'integration_status', label: 'Integration Status' }] : []),
    { key: 'stage', label: 'Stage' },
    { key: 'progress', label: 'Progress' },
    { key: 'start_end', label: 'Start & end date' },
    { key: 'project_id', label: 'ID' },
    { key: 'team_members', label: 'Team members' },
    { key: 'action', label: 'Action' },
  ];

  const defaultVisible = tableHeadMap.reduce((acc, col) => {
    if (col.key === 'project_id') {
      acc[col.key] = false;
    } else {
      acc[col.key] = true;
    }
    return acc;
  }, {} as Record<string, boolean>);

  const columns = [
    {
      key: "name",
      label: "Title",
      searchValue: (task: any) => ViewTitleSearchValue(task),
      render: (task: any) => <TitleCell row={task} />,
    },
    {
      key: "status",
      label: "Status",
      searchValue: (task: any) => getDashboardStatusLabel(task.status || ""),
      render: (row: any) => <StatusCell row={row} />,
    },
    // {
    //   key: 'status',
    //   label: 'Status',
    //   searchValue: (project: any) => getDashboardStatusLabel((simulation ? project?.sim_data?.status : project?.status) || ''),
    //   render: (project: any) => {
    //     const statusRaw = (simulation ? project?.sim_data?.status : project?.status) || '';
    //     const statusLower = String(statusRaw).toLowerCase();
    //     const isRiskContributing = statusLower === 'delayed' || statusLower === 'unallocated' || statusLower === 'blocked';
    //     const dotClass = getDashboardStatusColor(statusRaw);
    //     const label = getDashboardStatusLabel(statusRaw);
    //     return (
    //       <span className="inline-flex items-center gap-2 text-xs font-semibold">
    //         <span className={`w-2 h-2 rounded-full inline-block ${dotClass}`}></span>
    //         <span className="inline-flex items-center gap-1">
    //           {isRiskContributing && (
    //             <FiAlertTriangle className="text-red-500" title={`This is contributing to risk`} />
    //           )}
    //           <span>{label}</span>
    //         </span>
    //       </span>
    //     );
    //   },
    // },
    // {
    //   key: 'project_name',
    //   label: 'Name',
    //   searchValue: (project: any) => project.project_name,
    //   render: (project: any) => (
    //     <TruncatedText
    //       text={project.project_name}
    //       className="w-full truncate block"
    //       placement="top-start"
    //     />
    //   ),
    // },
    {
      key: "progress",
      label: "Progress",
      searchValue: (project: any) =>
        simulation
          ? (project?.sim_data?.progress ?? "")
          : (project?.progress ?? ""),
      render: (project: any) => {
        const progress = simulation
          ? project?.sim_data?.progress
          : project?.progress;
        return (
          <div className="flex justify-start">
            <Progress progress={progress} />
          </div>
        );
      },
      sortValue: (row: any) =>
        simulation ? (row?.sim_data?.progress ?? 0) : (row?.progress ?? 0),
    },
    ...(hasIntegrationType
      ? [
          {
            key: "integration_type",
            label: "Integration Type",
            searchValue: (project: any) =>
              project.integration_data?.integration_item_type,
            render: (project: any) => {
              const type = project.integration_data?.integration_item_type;
              return type ? (
                <span className="capitalize text-xs font-medium px-2.5 py-1 rounded bg-purple-100 text-purple-800 dark:bg-purple-200 dark:text-purple-900">
                  {type}
                </span>
              ) : (
                <span>-</span>
              );
            },
            sortValue: (project: any) =>
              (
                project.integration_data?.integration_item_type || ""
              ).toLowerCase(),
          },
        ]
      : []),
    ...(hasIntegrationType
      ? [
          {
            key: "integration_status",
            label: "Integration Status",
            searchValue: (project: any) =>
              project.integration_data?.integration_status,
            render: (project: any) => {
              const type = project.integration_data?.integration_status;
              return type ? (
                <span className="capitalize text-xs flex font-medium px-2.5 py-1">
                  {type}
                </span>
              ) : (
                <span>-</span>
              );
            },
            sortValue: (project: any) =>
              (
                project.integration_data?.integration_status || ""
              ).toLowerCase(),
          },
         
        ]
      : []),
    {
      key: "stage",
      label: "Stage",
      searchValue: (project: any) => project.stage,
      render: (project: any) => {
        const stage = (project.stage || "").toLowerCase();
        const bgClass =
          stageBgColorMap[stage] || "bg-gray-100 dark:bg-gray-800";
        const textClass =
          stageTextColorMap[stage] || "text-gray-700 dark:text-gray-200";
        return (
          <span
            className={`${bgClass} ${textClass} text-xs font-medium px-2.5 py-1 rounded`}
          >
            {project.stage || ""}
          </span>
        );
      },
    },
    {
      key: "team_members",
      label: "Team Members",
      searchValue: (project: any) => {
        const resourceIds = Array.isArray(project.members)
          ? project.members.map((m: any) =>
              typeof m === "object" && m !== null ? m.id : m,
            )
          : [];
        const names = resourceIds
          .map((id: any) => getResourceName(id))
          .filter(Boolean);
        return names.join(", ");
      },
      render: (project: any) => {
        const resourceIds = Array.isArray(project.members)
          ? project.members.map((m: any) =>
              typeof m === "object" && m !== null ? m.id : m,
            )
          : [];
        return (
          <Resources resources={resourceIds} projectId={project.project_id} />
        );
      },
    },
    {
      key: "start_end",
      label: "Start & end date",
      searchValue: (project: any) => {
        const start = simulation
          ? project?.sim_data?.start_date
          : project?.start_date;
        const end = simulation
          ? project?.sim_data?.end_date
          : project?.end_date;
        const shortStart = formatShortDate(start);
        const shortEnd = formatShortDate(end);
        const locStart = start ? new Date(start).toLocaleDateString() : "";
        const locEnd = end ? new Date(end).toLocaleDateString() : "";
        return [
          start || "",
          end || "",
          shortStart || "",
          shortEnd || "",
          locStart,
          locEnd,
        ]
          .filter(Boolean)
          .join(" ");
      },
      sortValue: (row: any) => {
        if (simulation) {
          return row?.sim_data?.start_date
            ? new Date(row?.sim_data?.start_date).getTime()
            : 0;
        }
        return row?.start_date ? new Date(row?.start_date).getTime() : 0;
      },
      render: (project: any) => {
        if (simulation) {
          if (project?.sim_data?.start_date && project?.sim_data?.end_date) {
            const shortStart = formatShortDate(project?.sim_data?.start_date);
            const shortEnd = formatShortDate(project?.sim_data?.end_date);
            return (
              <span
                title={`${project?.sim_data?.start_date} - ${project?.sim_data?.end_date}`}
                className="max-w-[120px] truncate block"
              >
                {shortStart} - {shortEnd}
              </span>
            );
          }
        } else {
          if (project?.start_date && project?.end_date) {
            const shortStart = formatShortDate(project?.start_date);
            const shortEnd = formatShortDate(project?.end_date);
            return (
              <span
                title={`${project?.start_date} - ${project?.end_date}`}
                className="max-w-[120px] truncate block"
              >
                {shortStart} - {shortEnd}
              </span>
            );
          }
        }

        return <span>-</span>;
      },
    },
    {
      key: "project_id",
      label: "ID",
      searchValue: (project: any) => project.project_id,
      headerRender: () => (
        <span className="flex items-center gap-1">
          <Tooltip
            className="bg-white dark:bg-gray-800"
            onClick={(e) => e.stopPropagation()}
            content={
              <div className="flex flex-col gap-1">
                <span className="lowercase text-gray-500 dark:text-gray-400">
                  Unique project identifier
                </span>
                <span className=" lowercase text-gray-500 dark:text-gray-400">
                  {" "}
                  (click to copy)
                </span>
              </div>
            }
            placement="top"
          >
            <span className="ml-1 text-gray-400">
              <HiOutlineInformationCircle className="inline-block w-4 h-4 align-middle" />
            </span>
          </Tooltip>
          ID
        </span>
      ),
      render: (project: any) => (
        <button
          onClick={(e) => handleCopyId(project.project_id, e)}
          className="truncate w-full block text-left hover:underline hover:bg-gray-100 dark:hover:bg-gray-700 px-1 py-0.5 rounded transition-colors duration-200 cursor-pointer relative group"
          title={`Click to copy: ${project.project_id}`}
        >
          <span
            className={`transition-colors duration-200 ${copiedId === project.project_id ? "text-green-600 dark:text-green-400" : ""}`}
          >
            {project.project_id}
          </span>
        </button>
      ),
    },
    {
      key: "action",
      label: "",
      render: (project: any) => {
        const handleViewDetails = () => {
          if (typeof handleRowClick === "function") {
            handleRowClick(project);
          }
        };
        const handleEdit = () => {
          if (typeof openEditProjectModal === "function") {
            openEditProjectModal(project);
          }
        };

        return (
          <div className="flex items-center justify-end gap-0">
            {/* Sim Pending Flag - glasses icon matching card view */}
            {project.is_sim_pending && (
              <CustomTooltip title="Simulation present">
                <div className="-mr-3 w-6 h-8 bg-[#467966] rounded-bl-2xl rounded-br-2xl flex items-center justify-center pt-1 cursor-help">
                  <FaGlasses className="text-white w-3 h-3" />
                </div>
              </CustomTooltip>
            )}
            <WorkItemOptions
              data={project}
              onViewDetails={() => handleProjectClick?.(project)}
            />
          </div>
        );
        return (
          <div
            className="flex justify-center items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <Options
              data={project}
              onDelete={onDelete}
              onViewDetails={handleViewDetails}
              openEditProjectModal={handleEdit}
              showOnHover={false}
              hideCopy={true}
              hideEdit={true}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col h-full dark:bg-[#111928] w-full pb-[50px]">
      <CadenceDataTable
        columns={columns}
        data={sortedData}
        extraFilterColumns={[
          {
            key: "entity_type",
            label: "Type",
          },
        ]}
        loading={loading}
        onRowClick={(row: any) => handleProjectClick(row.project_id, true)}
        defaultVisible={defaultVisible}
        columnVisibilityKey={`roadmap_level_table`}
        columnWeights={{
          name: 22,
          status: 10,
          progress: 8,
          integration_type: 10,
          integration_status: 10,
          stage: 8,
          team_members: 10,
          start_end: 12,
          project_id: 8,
          action: 8,
        }}
        emptyMessage={
          <div className="dark:text-white text-gray-800 flex justify-center items-center h-[50vh]">
            No projects found.
          </div>
        }
      />
    </div>
  );
};

export default ListViewTable;
